// Kunde anlegen oder bearbeiten — dasselbe Formular in der Liste und in der Akte.
import { useState } from 'react';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import { useSprache } from '../../../gemeinsam/lib/i18n';
import type { Kunde } from '../../../gemeinsam/lib/typen';
import { Feld, Hinweis, Karte, KartenKopf, Knopf } from '../../../gemeinsam/komponenten/Basis';

export const LEERER_KUNDE = {
  name: '', kundennummer: '', email: '', telefon: '',
  strasse: '', plz: '', ort: '', land: 'Deutschland', ustId: '', notiz: '',
  keineErinnerungen: false,
};
export type KundenFelder = typeof LEERER_KUNDE & { id?: string };

export const ausKunde = (k: Kunde): KundenFelder => ({
  ...LEERER_KUNDE,
  ...Object.fromEntries(Object.keys(LEERER_KUNDE).map(f => [f, (k as Record<string, unknown>)[f] ?? (LEERER_KUNDE as Record<string, unknown>)[f]])),
  id: k.id,
});

export default function KundenFormular({ start, beiFertig, beiAbbruch }: {
  start: KundenFelder;
  beiFertig: (k: Kunde, neu: boolean) => void;
  beiAbbruch: () => void;
}) {
  const { t } = useSprache();
  const [f, setF] = useState<KundenFelder>(start);
  const [fehler, setFehler] = useState<string | null>(null);
  const [sendet, setSendet] = useState(false);
  const setze = <K extends keyof KundenFelder>(k: K, w: KundenFelder[K]) => setF(x => ({ ...x, [k]: w }));

  async function speichern(e: React.FormEvent) {
    e.preventDefault();
    setFehler(null); setSendet(true);
    try {
      const { id, ...felder } = f;
      const k = id
        ? await api<Kunde>(`/customers/${id}`, { methode: 'PUT', daten: felder })
        : await api<Kunde>('/customers', { methode: 'POST', daten: felder });
      beiFertig(k, !id);
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : 'Speichern fehlgeschlagen.');
    } finally { setSendet(false); }
  }

  return (
    <Karte>
      <KartenKopf titel={f.id ? `${t('aktion.bearbeiten')} · ${f.name || t('feld.kunde')}` : t('kunden.neu')} />
      <form onSubmit={speichern} className="space-y-4 p-5">
        {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
        <div className="grid gap-4 sm:grid-cols-2">
          <Feld label={t('feld.name')} value={f.name} required autoFocus onChange={e => setze('name', e.target.value)} />
          <Feld label={t('kunden.nummer')} value={f.kundennummer} onChange={e => setze('kundennummer', e.target.value)} />
          <Feld label={t('feld.email')} type="email" value={f.email} onChange={e => setze('email', e.target.value)} />
          <Feld label={t('feld.telefon')} value={f.telefon} onChange={e => setze('telefon', e.target.value)} />
          <Feld label={t('feld.strasse')} value={f.strasse} onChange={e => setze('strasse', e.target.value)} />
          <div className="grid grid-cols-[100px_1fr] gap-3">
            <Feld label={t('feld.plz')} value={f.plz} onChange={e => setze('plz', e.target.value)} />
            <Feld label={t('feld.ort')} value={f.ort} onChange={e => setze('ort', e.target.value)} />
          </div>
          <Feld label={t('feld.ustId')} value={f.ustId} onChange={e => setze('ustId', e.target.value)} />
          <label className="flex items-start gap-2.5 self-end pb-2 text-sm text-slate-700 dark:text-slate-300">
            <input type="checkbox" checked={!!f.keineErinnerungen} onChange={e => setze('keineErinnerungen', e.target.checked)}
                   className="mt-0.5 h-4 w-4 rounded border-slate-300 dark:border-slate-600" />
            <span>{t('erinnerung.keineFuerKunden')}</span>
          </label>
          <label className="block sm:col-span-2">
            <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">{t('feld.notiz')}</span>
            <textarea value={f.notiz} onChange={e => setze('notiz', e.target.value)} rows={2} maxLength={2000}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-marke-500 focus:outline-none focus:ring-1 focus:ring-marke-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100" />
          </label>
        </div>
        <div className="flex gap-2">
          <Knopf type="submit" variante="primaer" laedt={sendet}>{t('aktion.speichern')}</Knopf>
          <Knopf type="button" onClick={beiAbbruch}>{t('aktion.abbrechen')}</Knopf>
        </div>
      </form>
    </Karte>
  );
}
